import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, ChevronRight, ChevronDown, X, History, 
  Sparkles, Zap, Bug, ArrowUp, Info, ExternalLink 
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentationPanel = ({ token, isOpen, onClose }) => {
  const [changelog, setChangelog] = useState([]);
  const [features, setFeatures] = useState({});
  const [versionInfo, setVersionInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('changelog');
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && token) {
      fetchDocumentation();
    }
  }, [isOpen, token]);

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      const [changelogRes, featuresRes, versionRes] = await Promise.all([
        axios.get(`${API_URL}/api/docs/changelog`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/docs/features`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/docs/version`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setChangelog(changelogRes.data.changelog || []);
      setFeatures(featuresRes.data.features || {});
      setVersionInfo(versionRes.data.version_info || {});
      
      // Ouvrir la dernière version par défaut
      if (changelogRes.data.changelog?.length > 0) {
        setExpandedVersion(changelogRes.data.changelog[0].version);
      }
    } catch (err) {
      console.error('Error fetching documentation:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'NEW': return <Sparkles className="w-4 h-4 text-green-400" />;
      case 'IMPROVED': return <ArrowUp className="w-4 h-4 text-cyan-400" />;
      case 'FIXED': return <Bug className="w-4 h-4 text-yellow-400" />;
      default: return <Zap className="w-4 h-4 text-slate-400" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'NEW': return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'IMPROVED': return 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30';
      case 'FIXED': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
      default: return 'text-slate-400 bg-slate-400/10 border-slate-400/30';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-900 border border-cyan-500/30 rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="documentation-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-cyan-400" />
              <div>
                <h2 className="font-mono font-bold text-cyan-400 text-lg">DOCUMENTATION & AIDE</h2>
                {versionInfo && (
                  <p className="text-xs text-slate-400">
                    Version {versionInfo.current_version} • {versionInfo.release_date}
                  </p>
                )}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="close-docs-btn"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-700">
            <button
              onClick={() => setActiveTab('changelog')}
              className={`px-4 py-3 font-mono text-sm transition-colors ${
                activeTab === 'changelog' 
                  ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-400/5' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <History className="w-4 h-4 inline mr-2" />
              CHANGELOG
            </button>
            <button
              onClick={() => setActiveTab('features')}
              className={`px-4 py-3 font-mono text-sm transition-colors ${
                activeTab === 'features' 
                  ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-400/5' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              FONCTIONNALITÉS
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(80vh-140px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full" />
              </div>
            ) : activeTab === 'changelog' ? (
              /* Changelog Tab */
              <div className="space-y-4">
                {changelog.map((release) => (
                  <div 
                    key={release.version}
                    className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedVersion(expandedVersion === release.version ? null : release.version)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded font-mono font-bold ${
                          release.type === 'major' 
                            ? 'bg-cyan-500/20 text-cyan-400' 
                            : 'bg-slate-600/50 text-slate-300'
                        }`}>
                          v{release.version}
                        </span>
                        <div className="text-left">
                          <div className="font-bold text-white">{release.title}</div>
                          <div className="text-xs text-slate-400">{release.date}</div>
                        </div>
                      </div>
                      {expandedVersion === release.version ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {expandedVersion === release.version && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-slate-700/50"
                        >
                          <div className="p-4 space-y-4">
                            {release.changes?.map((change, idx) => (
                              <div key={idx} className="space-y-2">
                                <div className="flex items-center gap-2">
                                  {getCategoryIcon(change.category)}
                                  <span className={`px-2 py-0.5 rounded text-xs font-mono border ${getCategoryColor(change.category)}`}>
                                    {change.category}
                                  </span>
                                  <span className="font-bold text-white">{change.title}</span>
                                </div>
                                <p className="text-sm text-slate-400 ml-6">{change.description}</p>
                                {change.details && (
                                  <ul className="ml-6 space-y-1">
                                    {change.details.map((detail, i) => (
                                      <li key={i} className="text-xs text-slate-500 flex items-start gap-2">
                                        <span className="text-cyan-500 mt-1">▸</span>
                                        {detail}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            ) : (
              /* Features Tab */
              <div className="space-y-4">
                {Object.entries(features).map(([id, feature]) => (
                  <div 
                    key={id}
                    className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFeature(expandedFeature === id ? null : id)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-cyan-500/10 rounded-lg">
                          <Sparkles className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-white">{feature.name}</div>
                          <div className="text-xs text-slate-400">
                            {feature.category} • Ajouté en v{feature.version_added}
                          </div>
                        </div>
                      </div>
                      {expandedFeature === id ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {expandedFeature === id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-slate-700/50"
                        >
                          <div className="p-4 space-y-4">
                            <p className="text-sm text-slate-300">{feature.description}</p>
                            
                            {feature.how_to_use && (
                              <div>
                                <h4 className="font-mono text-xs text-cyan-400 mb-2">COMMENT UTILISER</h4>
                                <ol className="space-y-1">
                                  {feature.how_to_use.map((step, i) => (
                                    <li key={i} className="text-sm text-slate-400">{step}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            
                            {feature.api_endpoints && (
                              <div>
                                <h4 className="font-mono text-xs text-cyan-400 mb-2">ENDPOINTS API</h4>
                                <div className="flex flex-wrap gap-2">
                                  {feature.api_endpoints.map((endpoint, i) => (
                                    <code key={i} className="px-2 py-1 bg-slate-700 rounded text-xs text-green-400">
                                      {endpoint}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {feature.limitations && (
                              <div>
                                <h4 className="font-mono text-xs text-yellow-400 mb-2">LIMITATIONS</h4>
                                <ul className="space-y-1">
                                  {feature.limitations.map((limit, i) => (
                                    <li key={i} className="text-xs text-slate-500 flex items-start gap-2">
                                      <Info className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                                      {limit}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DocumentationPanel;
